import { useEffect, useState } from 'react';
import { useOrg } from '../context/OrgContext';
import { getAdobeAccessToken, getImsUserId } from '../auth/adobeIms';
const PENDING_SWITCH_KEY = 'imsOrgSwitch_pending';
interface PendingSwitch {
  imsOrgId: string;
  destinationUserId: string;
}

// Find which cluster userId owns the given org (first match wins).
function resolveTargetUserId(imsOrgId: string, profileOrgMap: Record<string, string[]>): string | null {
  for (const [userId, orgIds] of Object.entries(profileOrgMap)) {
    if (orgIds.includes(imsOrgId)) return userId;
  }
  return null;
}
function Spinner() {
  return <svg width={12} height={12} viewBox="0 0 24 24" className="animate-spin" style={{
    color: 'rgba(255,255,255,0.5)'
  }} data-id="element-728" data-mp="37gqdbqaythvf">
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" data-id="element-729" data-mp="3bhttyf0u7hxp" />
      <path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" data-id="element-730" data-mp="32a47pzdkhne" />
    </svg>;
}
export function OrgSelector() {
  const {
    orgs,
    profileOrgMap,
    selectedOrg,
    setSelectedOrg,
    isLoadingOrgs
  } = useOrg();
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);

  // On mount: detect a post-redirect finalization.
  // After switchProfile() the IMS lib redirects back and fires onAccessToken.
  // At that point the app remounts; we clear the pending marker here.
  useEffect(() => {
    const raw = sessionStorage.getItem(PENDING_SWITCH_KEY);
    if (!raw || !getAdobeAccessToken()) return;
    try {
      const pending = JSON.parse(raw) as PendingSwitch;
      // New IMS session is active — clear the pending marker.
      // The OrgContext will re-fetch the catalog with the new token automatically.
      sessionStorage.removeItem(PENDING_SWITCH_KEY);

      // Find and select the org the user switched to, if it's in the new catalog.
      const targetOrg = orgs.find(o => o.imsOrgId === pending.imsOrgId);
      if (targetOrg) setSelectedOrg(targetOrg);
    } catch {
      sessionStorage.removeItem(PENDING_SWITCH_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once on mount

  if (isLoadingOrgs) {
    return <span style={{
      fontSize: 11,
      color: 'rgba(255,255,255,0.5)',
      padding: '4px 8px'
    }} data-id="element-731" data-mp="123n4h71et5uh">
        Loading orgs…
      </span>;
  }
  if (!orgs.length) return null;
  if (orgs.length === 1) {
    return <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      background: 'rgba(255,255,255,0.1)',
      borderRadius: 8,
      padding: '3px 10px',
      fontSize: 11,
      color: 'rgba(255,255,255,0.85)'
    }} data-id="element-732" data-mp="1c11lp8v5pynu">
        <span style={{
        opacity: 0.6
      }} data-id="element-733" data-mp="1puj1qjomwt94">Org:</span>
        <span style={{
        fontWeight: 600
      }} data-id="element-734" data-mp="1pt95m01cdv54">{selectedOrg?.orgName ?? orgs[0].orgName}</span>
      </div>;
  }
  async function handleOrgChange(imsOrgId: string) {
    const org = orgs.find(o => o.imsOrgId === imsOrgId);
    if (!org) return;

    // Always update app-local selection immediately.
    setSelectedOrg(org);
    setSwitchError(null);

    // Determine if the chosen org belongs to a different IMS account (T2E / cluster).
    const destinationUserId = resolveTargetUserId(imsOrgId, profileOrgMap);
    const currentUserId = getImsUserId();

    // No cross-account switch needed — org is in the current IMS session.
    if (!destinationUserId || !currentUserId || destinationUserId === currentUserId) return;
    if (!window.adobeIMS) return;
    try {
      setSwitching(true);

      // Purge the current token/profile before switching so IMSLib starts clean.
      const ims = window.adobeIMS as Record<string, unknown>;
      if (typeof ims.purgeTokenAndProfile === 'function') {
        (ims.purgeTokenAndProfile as () => void)();
      } else if (ims.tokenService && typeof (ims.tokenService as Record<string, unknown>).purge === 'function') {
        ((ims.tokenService as Record<string, unknown>).purge as () => void)();
      }

      // Persist the intent across the IMS redirect.
      const pending: PendingSwitch = {
        imsOrgId,
        destinationUserId
      };
      sessionStorage.setItem(PENDING_SWITCH_KEY, JSON.stringify(pending));

      // Trigger the real IMS session/profile switch — this will redirect.
      if (typeof ims.switchProfile === 'function') {
        await (ims.switchProfile as (profile: unknown, opts: unknown) => Promise<void>)(destinationUserId,
        //{ userId: destinationUserId },
        {
          explicit_profile_switch: true
        });
      }
    } catch (err) {
      sessionStorage.removeItem(PENDING_SWITCH_KEY);
      setSwitchError(err instanceof Error ? err.message : 'Profile switch failed');
      setSwitching(false);
    }
  }
  return <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: 6
  }} data-id="element-735" data-mp="352utoxlkg004">
      <span style={{
      fontSize: 11,
      color: 'rgba(255,255,255,0.55)'
    }} data-id="element-736" data-mp="3st77gp3hsqym">Org:</span>
      <select value={selectedOrg?.imsOrgId ?? ''} disabled={switching} onChange={e => void handleOrgChange(e.target.value)} style={{
      background: 'rgba(255,255,255,0.12)',
      border: '1px solid rgba(255,255,255,0.25)',
      borderRadius: 8,
      color: '#fff',
      fontSize: 11,
      fontWeight: 600,
      padding: '3px 8px',
      cursor: switching ? 'wait' : 'pointer',
      maxWidth: 200
    }} data-id="element-737" data-mp="1g49uzrb6e3h">
        {!selectedOrg && <option value="" disabled data-id="element-738" data-mp="137ni3md645oo">Select org…</option>}
        {orgs.map(o => <option key={o.imsOrgId} value={o.imsOrgId} style={{
        background: '#253044',
        color: '#fff'
      }} data-id="element-739" data-mp="y9cazje9c9a5">
            {o.orgName}
          </option>)}
      </select>

      {switching && <span style={{
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      fontSize: 10,
      color: 'rgba(255,255,255,0.5)'
    }} data-id="element-740" data-mp="1856vyl5yegg3">
          <Spinner data-id="element-741" data-mp="2oix6oarkw2yp" /> Switching…
        </span>}
      {switchError && <span style={{
      fontSize: 10,
      color: '#f87171'
    }} data-id="element-742" data-mp="2jkc5a80lbd8o">{switchError}</span>}
    </div>;
}